import {classList, dataset, listen} from 'nuffjs';
import {CollapsableHeader} from './collapsable-header';

const ESC_KEYCODE = 27;

/**
 * @property {string} searchActiveClass=searchActive class to set when the search input is focused
 * @property {string} searchBoxAttr=searchBox data-attribute to identify the nested search container
 *
 * @memberof HeaderWithSearch
 * @extends CollapsableHeader.conf
 */
const conf = Object.assign({}, CollapsableHeader.conf, {
	searchActiveClass: 'searchActive',
	searchBoxAttr: 'searchBox',
});

/**
 * @classdesc Collapsable header with the addition of a {@link Search} component inside. Reacts to
 * the focus being set / unset to the search field.
 *
 * @class HeaderWithSearch
 * @extends {CollapsableHeader}
 * @version 1.0.0
 * @path
 *
 */
class HeaderWithSearch extends CollapsableHeader {
	static conf = conf;

	/**
	 * @property {number} outInterval=100 interval to whait before considering the focus to have left the input
	 * @property {boolean} emptyOnBlur=true flag to clear the search results on blur
	 *
	 * @static
	 * @memberof HeaderWithSearch
	 * @extends CollapsableHeader.defaultOpts
	 */
	static defaultOpts = Object.assign({}, CollapsableHeader.defaultOpts, {
		outInterval: 100,
		emptyOnBlur: true,
	});

	outTimeout = null;
	insideSearchBox = false;

	firstConnectedCallback() {
		this.searchBox = this.querySelector(dataset.selector(conf.searchBoxAttr));
		super.firstConnectedCallback();
		if (this.searchBox) {
			this.inputs = this.searchBox.querySelectorAll('input[type="text"],input[type="search"]');
		}
	}

	/**
	 * Setup additional listeners to monitor the focus on the search box
	 *
	 * @memberof HeaderWithSearch
	 */
	initListeners() {
		super.initListeners();
		if (this.searchBox) {
			this.listeners.push(listen(this.searchBox, 'focusin', this.onFocusInSearch.bind(this)));
			this.listeners.push(listen(this.searchBox, 'focusout', this.onFocusOutSearch.bind(this)));
			this.listeners.push(
				listen(this.searchBox, 'keydown', this.onKeyPress.bind(this), {selector: 'input'})
			);
		}
	}

	/**
	 * we handle the ESC key to close the box as well
	 *
	 * @param {*} event
	 * @memberof HeaderWithSearch
	 */
	onKeyPress(event) {
		if (event.which === ESC_KEYCODE) {
			event.target.blur();
		}
	}

	/**
	 * Handle the focusin toggling the box in
	 *
	 * @param {*} event
	 * @memberof HeaderWithSearch
	 */
	onFocusInSearch(event) {
		if (this.outTimeout) {
			clearTimeout(this.outTimeout);
			this.outTimeout = null;
		}
		this.toggleInSearchBox(true);
	}

	/**
	 * Handle the focus out, and toggle the box out (with delay)
	 *
	 * @param {Event} event the focusout event
	 * @memberof HeaderWithSearch
	 */
	onFocusOutSearch(event) {
		this.outTimeout = setTimeout(() => this.toggleInSearchBox(false), this.opts.outInterval);
	}

	/**
	 * Toggle the class and eventually reset the search results based on focus / blur on the search
	 *
	 * @param {boolean} isIn flag to control if the search is active or not
	 * @memberof HeaderWithSearch
	 */
	toggleInSearchBox(isIn) {
		if (isIn !== this.insideSearchBox) {
			this.insideSearchBox = isIn;
			classList(this).toggle(conf.searchActiveClass, isIn);
			if (!isIn && this.opts.emptyOnBlur) {
				for (const input of this.inputs) {
					input.value = '';
					const event = new Event('input', {bubbles: true});
					input.dispatchEvent(event);
				}
			}
		}
	}
}

export {HeaderWithSearch};
